import { Avatar, type SxProps, type Theme } from '@mui/material';
import type { GetCommentsQueryData } from '@/generated/graphql';

type CommentAuthor =
  | GetCommentsQueryData['comments'][number]['author']
  | undefined;

export function authorName(author: CommentAuthor): string {
  if (!author) return 'Unknown';
  return (
    author.artistName ||
    `${author.firstName} ${author.lastName}`.trim() ||
    'Unknown'
  );
}

/** Up to two uppercase letters from the display name; "?" when unknown. */
export function authorInitials(author: CommentAuthor): string {
  if (!author) return '?';
  const name =
    author.artistName || `${author.firstName} ${author.lastName}`.trim();
  return (
    name
      .split(/\s+/)
      .map((p) => p.match(/[\p{L}\p{N}]/u)?.[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('') || '?'
  );
}

interface AuthorAvatarProps {
  author: CommentAuthor;
  size?: number;
  /** Hide from assistive tech when the author's name is rendered beside it. */
  decorative?: boolean;
  sx?: SxProps<Theme>;
}

/** Shared comment-author avatar: image or two-letter initials. */
export function AuthorAvatar({
  author,
  size = 20,
  decorative = false,
  sx,
}: AuthorAvatarProps) {
  return (
    <Avatar
      src={author?.image || undefined}
      alt=""
      aria-hidden={decorative || undefined}
      sx={[
        {
          width: size,
          height: size,
          // Half the diameter (MUI's own ratio): two capitals at 11px measured
          // ~14-23px wide, overflowing a 20px circle; 10px fits typical pairs.
          fontSize: Math.round(size * 0.5),
          fontWeight: 600,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {authorInitials(author)}
    </Avatar>
  );
}
